import React, { useState } from 'react';
import { Flame, Zap, Snowflake, Sparkles, RefreshCw } from 'lucide-react';
import { scoreLead } from '../../services/aiService';

export default function AIIntentBadge({ leadId, score: initialScore, reason: initialReason, onScored, compact = false }) {
  const [score, setScore] = useState(initialScore);
  const [reason, setReason] = useState(initialReason);
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleCalculateScore = async (e) => {
    if (e) e.stopPropagation();
    if (!leadId) return;
    setLoading(true);
    try {
      const res = await scoreLead(leadId);
      if (res.success) {
        setScore(res.score);
        setReason(res.reason);
        if (onScored) onScored(res.score, res.reason);
      }
    } catch (err) {
      console.error('Error calculating AI score:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '11px',
        background: 'var(--surface-3, rgba(255, 255, 255, 0.08))',
        color: 'var(--muted, #94a3b8)',
        border: '1px solid var(--border, rgba(255, 255, 255, 0.1))'
      }}>
        <RefreshCw size={11} className="animate-spin" />
        {!compact && <span>Scoring...</span>}
      </span>
    );
  }

  if (!score) {
    return (
      <button
        onClick={handleCalculateScore}
        title="Calculate AI Purchase Intent Score"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 8px',
          borderRadius: '9999px',
          fontSize: '11px',
          fontWeight: '600',
          background: 'rgba(99, 102, 241, 0.12)',
          color: '#818cf8',
          border: '1px solid rgba(99, 102, 241, 0.25)',
          cursor: 'pointer',
          transition: 'all 0.15s ease'
        }}
      >
        <Sparkles size={11} />
        {!compact && <span>AI Score</span>}
      </button>
    );
  }

  let badgeStyle = {};
  let Icon = Sparkles;

  if (score === 'HOT') {
    badgeStyle = {
      background: 'rgba(239, 68, 68, 0.15)',
      color: '#f87171',
      border: '1px solid rgba(239, 68, 68, 0.3)',
    };
    Icon = Flame;
  } else if (score === 'WARM') {
    badgeStyle = {
      background: 'rgba(245, 158, 11, 0.15)',
      color: '#fbbf24',
      border: '1px solid rgba(245, 158, 11, 0.3)',
    };
    Icon = Zap;
  } else {
    badgeStyle = {
      background: 'rgba(56, 189, 248, 0.15)',
      color: '#38bdf8',
      border: '1px solid rgba(56, 189, 248, 0.3)',
    };
    Icon = Snowflake;
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setShowTooltip(!showTooltip); }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '2px 8px',
          borderRadius: '9999px',
          fontSize: '11px',
          fontWeight: '700',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          ...badgeStyle
        }}
      >
        <Icon size={11} />
        <span>{score}</span>
      </button>

      {showTooltip && reason && (
        <div style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginBottom: '6px',
          width: '200px',
          padding: '8px 10px',
          background: '#0f172a',
          color: '#f8fafc',
          fontSize: '11px',
          lineHeight: '1.4',
          borderRadius: '8px',
          border: '1px solid #334155',
          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.5)',
          zIndex: 99,
          pointerEvents: 'none'
        }}>
          <div style={{ fontWeight: '700', marginBottom: '2px', color: '#818cf8', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Sparkles size={11} /> Intent AI Rationale
          </div>
          {reason}
        </div>
      )}
    </div>
  );
}
